import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import {
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";

export type ConfirmDialogVariant = "danger" | "warning" | "info";

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmDialogVariant;
  onConfirm: () => void;
  onCancel: () => void;
}

const VARIANT_CONFIG = {
  danger: {
    icon: ExclamationTriangleIcon,
    iconBg: "bg-red-900/30",
    iconColor: "text-red-400",
    confirmClass: "bg-red-900/30 border border-red-700/50 text-red-400 hover:bg-red-900/50 hover:text-red-300",
    defaultConfirmLabel: "Delete",
  },
  warning: {
    icon: ExclamationCircleIcon,
    iconBg: "bg-amber-900/30",
    iconColor: "text-amber-400",
    confirmClass: "bg-amber-900/30 border border-amber-700/50 text-amber-400 hover:bg-amber-900/50 hover:text-amber-300",
    defaultConfirmLabel: "Confirm",
  },
  info: {
    icon: InformationCircleIcon,
    iconBg: "bg-squawk-blue/20",
    iconColor: "text-squawk-blue",
    confirmClass: "bg-squawk-blue/20 border border-squawk-blue/50 text-squawk-blue hover:bg-squawk-blue/30",
    defaultConfirmLabel: "Confirm",
  },
};

export default function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmLabel,
  cancelLabel = "Cancel",
  variant = "danger",
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const config = VARIANT_CONFIG[variant];
  const Icon = config.icon;

  return (
    <Dialog open={isOpen} onClose={onCancel} className="relative z-[100] no-drag">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-900/80 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150"
      />
      <div className="fixed inset-0 z-10 flex items-center justify-center p-4">
        <DialogPanel
          transition
          className="w-full max-w-md transform overflow-hidden rounded-lg bg-gray-800 shadow-xl transition-all data-[closed]:translate-y-2 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[closed]:scale-95"
        >
          <div className="p-5">
            <div className="flex gap-4">
              {/* Icon */}
              <div className={clsx("flex size-10 shrink-0 items-center justify-center rounded-full", config.iconBg)}>
                <Icon className={clsx("size-5", config.iconColor)} />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <DialogTitle className="text-sm font-semibold text-gray-100">
                  {title}
                </DialogTitle>
                <p className="mt-1.5 text-sm text-gray-400 leading-relaxed">
                  {message}
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={onCancel}
                className="px-4 py-2 text-sm font-medium rounded-md bg-gray-700/50 border border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-gray-100 transition-colors"
              >
                {cancelLabel}
              </button>
              <button
                onClick={onConfirm}
                className={clsx("px-4 py-2 text-sm font-medium rounded-md transition-colors", config.confirmClass)}
              >
                {confirmLabel ?? config.defaultConfirmLabel}
              </button>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
